import { noop, trimEnd } from 'lodash';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import { Link } from '@zocdoc-frontend-common/components-link';
import { Stars } from '@zocdoc-frontend-common/components-stars';
import {
    Column,
    Row,
    columnOffset,
    columnSize,
} from '@zocdoc-frontend-common/styles-grid';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    Metrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import {
    slate30,
    slate10,
    slate80,
    white,
} from '@zocdoc-mezzanine/foundations-colors';
import columnWidth from '../../../utils/columnWidth';
import VideoVisitBanner from '../../components/VideoVisitBanner';
import {
    DENTAL_INSURANCE_SPECIALTIES,
    TRACKING_CONSTANTS,
} from '../../constants';
import SecureBookingMessage from '../SecureBookingMessage';

function isADentistrySpecialty(specialtyId) {
    return DENTAL_INSURANCE_SPECIALTIES.has(specialtyId);
}

let logged = false;
export function testSetLogged(setTo) {
    logged = setTo;
}

const logPageLoadTime = (props) => {
    const { professional } = props;
    if (professional && !logged) {
        logged = true;
    }
};

/**
 * @param {string} formattedAppointmentTime
 * @param {boolean} hasProfessional
 * @param {string} googleUrl
 * @param {string} iCalUrl
 * @param {Object} location
 * @param {function(): void} onAddToCalendarClick
 * @param {function(): void} onGoogleClick
 * @param {function(): void} onIcalClick
 * @param {function(): void} onOutlookClick
 * @param {string} outlookUrl
 * @param {string} phoneNumber
 * @param {function(): void} savePatientName
 * @param {function(professional: Object): void} saveProfessional
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {boolean} showOnlyLocationForXxsmall
 * @param {boolean} useBookingCompleteView
 * @returns {JSX.Element}
 * @constructor
 */
export class DoctorCardView extends PureComponent {
    static defaultProps = {
        sendMetric: noop,
    };

    state = {
        showLinks: false,
    };

    shouldHideRating() {
        const { professional } = this.props;
        return (
            typeof professional.rating !== 'number' || professional.rating < 1
        );
    }

    renderBodyChildRow(label, children, showForSmall) {
        return (
            <CardRow hideMobile={showForSmall}>
                <Label>{label}</Label>
                <Body>{children}</Body>
            </CardRow>
        );
    }

    renderBodyChildRowMobileOnly(label, children) {
        return (
            <CardRow hideDesktop>
                <Label>{label}</Label>
                <Body>{children}</Body>
            </CardRow>
        );
    }

    renderAddress2(address2) {
        if (address2) {
            return (
                <span data-test="doctor-card-view-location-address2">
                    {address2},{' '}
                </span>
            );
        }
    }

    renderLocationForDoctorHeader() {
        const { location } = this.props;

        if (location === undefined) {
            return undefined;
        }

        const onClickFn = () => {
            this.setProviderLocationCardClickLocation(
                TRACKING_CONSTANTS.Location
            );
        };

        if (location.isVirtual) {
            return <Location onClick={onClickFn}>Video visit</Location>;
        }

        const address1 = trimEnd(location.address1, ' ,');
        const address2 = trimEnd(location.address2, ' ,');

        return (
            <Location onClick={onClickFn}>
                <span
                    itemProp="location"
                    itemScope
                    itemType="http://schema.org/Place"
                >
                    <span
                        itemProp="address"
                        itemScope
                        itemType="http://schema.org/PostalAddress"
                    >
                        <span itemProp="streetAddress">
                            <span data-test="doctor-card-view-location-address1">
                                {address1},{' '}
                            </span>
                            {this.renderAddress2(address2)}
                        </span>{' '}
                        <span data-test="doctor-card-view-location-city-state-zip">
                            <span itemProp="addressLocality">
                                {location.city}
                            </span>
                            ,{' '}
                            <span itemProp="addressRegion">
                                {location.state}
                            </span>{' '}
                            <span itemProp="postalCode">
                                {location.zipCode}
                            </span>
                            <meta content="USA" itemProp="addressCountry" />
                        </span>
                    </span>
                </span>
            </Location>
        );
    }

    maybeRenderLocation() {
        const { location } = this.props;

        if (location === undefined) {
            return undefined;
        }

        const address1 = trimEnd(location.address1, ' ,');
        const address2 = trimEnd(location.address2, ' ,');

        return this.renderBodyChildRow(
            'Location',
            <span
                itemProp="location"
                itemScope
                itemType="http://schema.org/Place"
                onClick={() => {
                    this.setProviderLocationCardClickLocation(
                        TRACKING_CONSTANTS.Location
                    );
                }}
            >
                <span
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                >
                    <span itemProp="streetAddress">
                        <span data-test="doctor-card-view-location-address1">
                            {address1},{' '}
                        </span>
                        {this.renderAddress2(address2)}
                    </span>{' '}
                    <span data-test="doctor-card-view-location-city-state-zip">
                        <span itemProp="addressLocality">{location.city}</span>,{' '}
                        <span itemProp="addressRegion">{location.state}</span>{' '}
                        <span itemProp="postalCode">{location.zipCode}</span>
                        <meta content="USA" itemProp="addressCountry" />
                    </span>
                </span>
            </span>,
            true
        );
    }

    maybeRenderContactInformation() {
        const { phoneNumber } = this.props;

        if (
            phoneNumber === undefined ||
            !phoneNumber.replace(/\s/g, '').length
        ) {
            return undefined;
        }

        const childRow = (
            <p>
                {this.renderBodyChildRow(
                    'Office Number',
                    <span data-test="doctor-card-office-number-web">
                        {phoneNumber}
                    </span>,
                    true
                )}
                {this.renderBodyChildRowMobileOnly(
                    'Office Number',
                    <span>
                        <span data-test="doctor-card-office-number-mobile">
                            {phoneNumber}
                        </span>
                        <p>
                            <Link
                                data-test="call-office-button"
                                href={`tel:${phoneNumber}`}
                                onClick={this.props.trackClickCall}
                            >
                                Call Office
                            </Link>
                        </p>
                    </span>,
                    false
                )}
            </p>
        );

        return childRow;
    }

    maybeRenderAppointmentTimeInCardBody() {
        const { formattedAppointmentTime, showOnlyLocationForMobile } =
            this.props;

        if (formattedAppointmentTime) {
            return (
                <div>
                    {this.renderBodyChildRow(
                        'Appointment',
                        <span
                            data-test="doctor-card-view-appointment-time"
                            data-uem-id="appointment-time"
                            onClick={() => {
                                this.setProviderLocationCardClickLocation(
                                    TRACKING_CONSTANTS.AppointmentDate
                                );
                            }}
                        >
                            {formattedAppointmentTime}
                        </span>,

                        showOnlyLocationForMobile
                    )}
                </div>
            );
        }
    }

    toggleLinks() {
        this.setState({
            showLinks: !this.state.showLinks,
        });

        this.props.onAddToCalendarClick();
    }

    onClick = (trackEventFn) => {
        trackEventFn();
        this.toggleLinks();
    };

    renderCalendarLinks(showLinks) {
        if (showLinks) {
            return (
                <div>
                    <p>
                        <Link
                            data-test="outlook-button"
                            href={this.props.outlookUrl}
                            onClick={() => {
                                this.onClick(this.props.onOutlookClick);
                            }}
                        >
                            Outlook
                        </Link>
                    </p>
                    <p>
                        <Link
                            data-test="ical-button"
                            href={this.props.iCalUrl}
                            onClick={() => {
                                this.onClick(this.props.onIcalClick);
                            }}
                        >
                            iCal
                        </Link>
                    </p>
                    <p>
                        <Link
                            data-test="google-button"
                            href={this.props.googleUrl}
                            onClick={() => {
                                this.onClick(this.props.onGoogleClick);
                            }}
                        >
                            Google
                        </Link>
                    </p>
                </div>
            );
        } else return undefined;
    }

    maybeRenderAppointmentTimeInCardHead() {
        const { formattedAppointmentTime, showDoctorHeader } = this.props;

        return (
            <HeaderAppointmentTime
                showDoctorHeader={showDoctorHeader}
                data-test="doctor-card-view-appointment-time"
                data-uem-id="appointment-time"
                onClick={() => {
                    this.setProviderLocationCardClickLocation(
                        TRACKING_CONSTANTS.AppointmentDate
                    );
                }}
            >
                {formattedAppointmentTime}
            </HeaderAppointmentTime>
        );
    }

    maybeRenderPatientInfo() {
        const { patientName, showOnlyLocationForMobile } = this.props;
        if (patientName === undefined) {
            return this.renderBodyChildRow(
                'Patient',
                <span
                    className="exclude-from-playback"
                    data-test="doctor-card-view-patient"
                    onClick={() => {
                        this.setProviderLocationCardClickLocation(
                            TRACKING_CONSTANTS.PatientName
                        );
                    }}
                >
                    TBD
                </span>,
                showOnlyLocationForMobile
            );
        }

        return this.renderBodyChildRow(
            'Patient',
            <span
                className="exclude-from-playback"
                data-test="doctor-card-view-patient"
            >
                {patientName}
            </span>,
            showOnlyLocationForMobile
        );
    }

    maybeRenderVisitReason() {
        const { visitReason, showOnlyLocationForMobile } = this.props;

        if (visitReason === undefined) {
            return undefined;
        }

        return this.renderBodyChildRow(
            'Visit reason',
            <span
                data-test="doctor-card-view-visit-reason"
                onClick={() => {
                    this.setProviderLocationCardClickLocation(
                        TRACKING_CONSTANTS.VisitReason
                    );
                }}
            >
                {visitReason}
            </span>,
            showOnlyLocationForMobile
        );
    }

    maybeRenderSecureBookingMessage() {
        const { showDoctorHeader } = this.props;
        if (!showDoctorHeader) {
            return <SecureBookingMessage data-test="secure-booking-message" />;
        }
    }

    renderSpecialty() {
        const { professional } = this.props;

        return (
            <Specialty
                key="specialty"
                data-test="doctor-card-view-doctor-specialty"
                onClick={() => {
                    this.setProviderLocationCardClickLocation(
                        TRACKING_CONSTANTS.Specialty
                    );
                }}
            >
                {professional.specialty}
            </Specialty>
        );
    }

    renderSpecialtyAndRatings() {
        const { professional } = this.props;
        const rating = this.shouldHideRating() ? null : (
            <Rating
                key="rating"
                data-test="appointment-card-rating"
                onClick={() =>
                    this.setProviderLocationCardClickLocation(
                        TRACKING_CONSTANTS.Ratings
                    )
                }
            >
                <Stars height={24} rating={parseFloat(professional.rating)} />
            </Rating>
        );
        return [this.renderSpecialty(), rating];
    }

    componentWillReceiveProps(nextProps) {
        const { professional } = nextProps;
        const { saveProfessional, hasProfessional } = this.props;

        if (professional && !hasProfessional) {
            saveProfessional(professional);
        }

        logPageLoadTime(nextProps);
    }

    componentDidMount() {
        logPageLoadTime(this.props);
    }

    getProfessionalNameTitle() {
        const { professional } = this.props;
        return `${professional.firstName} ${professional.lastName}, ${professional.title}`;
    }

    renderDoctorCard() {
        const { professional } = this.props;
        const professionalNameTitle = this.getProfessionalNameTitle();
        const itemType = isADentistrySpecialty(professional.specialtyId)
            ? 'http://schema.org/Dentist'
            : 'http://schema.org/Physician';

        return (
            <div>
                <Wrapper
                    data-test="doctor-card"
                    itemScope
                    itemType={itemType}
                    onClick={this.trackProviderLocationCardClick}
                >
                    <Metrics component="ProviderCard">
                        <Content>
                            <Row>
                                <HeaderInfo>
                                    <PhotoWrapper
                                        data-test="doctor-card-photo-wrapper"
                                        photoIsCircle={
                                            professional.showPhotoWithCircleCss
                                        }
                                        onClick={() => {
                                            this.setProviderLocationCardClickLocation(
                                                TRACKING_CONSTANTS.Photo
                                            );
                                        }}
                                    >
                                        <img
                                            alt={professionalNameTitle}
                                            role="presentation"
                                            src={professional.image}
                                        />
                                    </PhotoWrapper>
                                </HeaderInfo>
                                <AppointmentDetails>
                                    <Name
                                        data-test="doctor-card-view-doctor-name"
                                        itemProp="name"
                                        onClick={() => {
                                            this.setProviderLocationCardClickLocation(
                                                TRACKING_CONSTANTS.Name
                                            );
                                        }}
                                    >
                                        {professionalNameTitle}
                                    </Name>
                                    {this.maybeRenderAppointmentTimeInCardHead()}
                                    {this.renderSpecialtyAndRatings()}
                                </AppointmentDetails>
                            </Row>

                            <div>
                                {this.maybeRenderLocation()}
                                {this.maybeRenderContactInformation()}
                                {this.maybeRenderAppointmentTimeInCardBody()}
                                {this.maybeRenderPatientInfo()}
                                {this.maybeRenderVisitReason()}
                            </div>
                        </Content>
                    </Metrics>
                </Wrapper>
                {this.maybeRenderSecureBookingMessage()}
            </div>
        );
    }

    renderDoctorCardBookingComplete() {
        const { professional } = this.props;

        const professionalNameTitle = `${professional.firstName} ${professional.lastName}, ${professional.title}`;

        return (
            <NoCheckInWrapper>
                <Content>
                    <Row>
                        <HeaderInfo>
                            <PhotoWrapper
                                data-test="doctor-card-photo-wrapper"
                                photoIsCircle={
                                    professional.showPhotoWithCircleCss
                                }
                            >
                                <img
                                    alt={professionalNameTitle}
                                    role="presentation"
                                    src={professional.image}
                                />
                            </PhotoWrapper>
                        </HeaderInfo>
                        <NoCheckInName data-test="doctor-card-view-doctor-name">
                            {professionalNameTitle}
                        </NoCheckInName>
                        {this.renderSpecialty()}
                    </Row>

                    <div>
                        <HorizontalRule />
                        {this.maybeRenderAppointmentTimeInCardBody()}
                        {this.maybeRenderPatientInfo()}
                        {this.maybeRenderVisitReason()}
                    </div>
                </Content>
            </NoCheckInWrapper>
        );
    }

    renderDoctorCardHeader() {
        const { professional, location } = this.props;

        const professionalName = `${professional.firstName} ${professional.lastName}`;
        const professionalNameTitle = `${professionalName}, ${professional.title}`;

        const itemType = isADentistrySpecialty(professional.specialtyId)
            ? 'http://schema.org/Dentist'
            : 'http://schema.org/Physician';

        return (
            <div data-test="doctor-card-header">
                <DoctorHeaderWrapper
                    data-test="doctor-card"
                    itemScope
                    itemType={itemType}
                    onClick={this.trackProviderLocationCardClick}
                >
                    <Metrics component="ProviderCard">
                        <Content>
                            <DoctorHeaderRow>
                                <HeaderInfoDoctorHeader>
                                    <DoctorHeaderPhotoWrapper
                                        data-test="doctor-card-header-photo-wrapper"
                                        photoIsCircle={
                                            professional.showPhotoWithCircleCss
                                        }
                                        onClick={() => {
                                            this.setProviderLocationCardClickLocation(
                                                TRACKING_CONSTANTS.Photo
                                            );
                                        }}
                                    >
                                        <img
                                            alt={professionalNameTitle}
                                            role="presentation"
                                            src={professional.image}
                                        />
                                    </DoctorHeaderPhotoWrapper>
                                </HeaderInfoDoctorHeader>
                                <AppointmentDetailsDoctorHeader>
                                    <Name
                                        data-test="doctor-card-header-doctor-name"
                                        itemProp="name"
                                        onClick={() => {
                                            this.setProviderLocationCardClickLocation(
                                                TRACKING_CONSTANTS.Name
                                            );
                                        }}
                                    >
                                        {professionalNameTitle}
                                    </Name>
                                    {this.maybeRenderAppointmentTimeInCardHead()}
                                    {this.renderLocationForDoctorHeader()}
                                </AppointmentDetailsDoctorHeader>
                            </DoctorHeaderRow>
                        </Content>
                    </Metrics>
                </DoctorHeaderWrapper>
                {location && location.isVirtual ? (
                    <DoctorHeaderRow>
                        <VideoVisitBanner
                            professionalName={professionalName}
                            virtualVisitType={location.virtualVisitType}
                        />
                    </DoctorHeaderRow>
                ) : (
                    <span data-test="no-video-visit-banner"></span>
                )}
                {this.maybeRenderSecureBookingMessage()}
            </div>
        );
    }

    render() {
        const { professional, useBookingCompleteView, showDoctorHeader } =
            this.props;

        if (professional === undefined) {
            return <Empty />;
        }

        if (showDoctorHeader) {
            return this.renderDoctorCardHeader();
        } else {
            return useBookingCompleteView
                ? this.renderDoctorCardBookingComplete()
                : this.renderDoctorCard();
        }
    }

    /*
     *  We only want to fire one click tracking event per provider location card click.
     *  When an inner element is clicked, set the click location on 'this'.
     *  The event will bubble up to the outer container, and fire the event using
     *  the click location from the inner element or the generic 'ProviderLocationCard'
     *  click location.
     */
    setProviderLocationCardClickLocation = (clickLocation) => {
        this.doctorCardClickLocation = clickLocation;
    };

    trackProviderLocationCardClick = () => {
        const clickLocation =
            this.doctorCardClickLocation || TRACKING_CONSTANTS.ProviderCard;
        this.props.sendMetric({
            action: 'Click ProviderCard',
            element: clickLocation,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        this.doctorCardClickLocation = null;
    };
}

const HeaderAppointmentTime = styled.span`
    ${(p) =>
        p.showDoctorHeader
            ? css`
                  font: 14px/20px ${FontFamily.SharpSansMedium};
              `
            : css`
                  font: 13px/20px ${FontFamily.SharpSansMedium};
                  ${breakpoint.small} {
                      display: none;
                  }
              `}
`;

const PhotoWrapper = styled.div`
    border-color: ${(p) => (p.photoIsCircle ? white : 'transparent')};
    border-radius: ${(p) => (p.photoIsCircle ? '50%' : 'none')};
    float: left;
    overflow: ${(p) => (p.photoIsCircle ? 'hidden' : 'visible')};
    width: 44px;
    height: 44px;

    ${breakpoint.small} {
        float: none;
        width: 64px;
        height: 64px;
    }
`;

const Wrapper = styled.div`
    background-color: ${white};
    border-top: solid 1px ${slate30};
    border-bottom: solid 1px ${slate30};
    padding: 20px ${columnWidth(1)};

    ${breakpoint.small} {
        border: solid 1px ${slate30};
        padding: 20px ${columnWidth(2)};
    }
`;

const NoCheckInWrapper = styled.div`
    background-color: ${white};
    border-top: solid 1px ${slate30};
    border-bottom: solid 1px ${slate30};
    padding: 20px ${columnWidth(1)};

    ${breakpoint.medium} {
        background-color: ${slate10};
        border: solid 1px ${slate30};
        min-width: 250px;
        padding: 20px ${columnWidth(2)};
    }
`;

const HeaderInfo = styled.div`
    margin-right: 16px;
    width: auto;
`;

const AppointmentDetails = styled(Column).attrs({ end: true })`
    ${columnOffset(1)};
    ${columnSize(16)};

    ${breakpoint.small} {
        ${columnOffset(0)};
        ${columnSize(21)};
        margin-top: 16px;
    }
`;

const Empty = styled.div`
    &::after {
        content: '\00a0';
    }
`;

const Content = styled.div`
    margin: 0 auto;
`;

const HorizontalRule = styled(Column)`
    border: none;
    border-top: 2px solid ${slate30};
    margin: 20px 0;
`;

const DoctorHeaderWrapper = styled.div`
    background-color: ${white};
    border-bottom: solid 1px ${slate30};
    padding: 20px ${columnWidth(1)};
    ${breakpoint.small} {
        padding: 20px 0;
    }
    ${breakpoint.xlarge} {
        padding: 20px ${columnWidth(1)};
    }
`;

const DoctorHeaderRow = styled(Row)`
    margin: 0 auto;
    max-width: 504px;
`;

const DoctorHeaderPhotoWrapper = styled.div`
    border-color: ${(p) => (p.photoIsCircle ? white : 'transparent')};
    border-radius: ${(p) => (p.photoIsCircle ? '50%' : 'none')};
    float: left;
    overflow: ${(p) => (p.photoIsCircle ? 'hidden' : 'visible')};
    width: 64px;
    height: 64px;
`;

const HeaderInfoDoctorHeader = styled.div`
    float: left;
    margin-right: 16px;
    width: auto;
`;

const AppointmentDetailsDoctorHeader = styled(Column).attrs({ end: true })`
    float: right;
    ${columnSize(16)};
    ${breakpoint.small} {
        ${columnOffset(0)};
        ${columnSize(18)};
    }
`;

const sharedStyle = css`
    box-sizing: border-box;
    display: block;
    min-height: 24px;
`;

const sharedNameStyle = css`
    font: 15px/1.2 ${FontFamily.SharpSansSemibold};
    padding-bottom: calc(24px - 1em * 1.2);

    ${breakpoint.small} {
        font: 18px/1.2 ${FontFamily.SharpSansBold};
    }
`;

const Name = styled.span`
    ${sharedStyle};
    ${sharedNameStyle};
`;

const NoCheckInName = styled.div`
    ${sharedStyle};
    ${sharedNameStyle};

    float: left;
    font-size: 18px;
    margin-left: 10px;
    margin-top: 10px;

    ${breakpoint.small} {
        float: none;
        margin-left: 0;
    }
`;

const Specialty = styled.div`
    color: ${slate80};
    font: 15px/20px ${FontFamily.SharpSansBold};
    display: none;
    min-height: 24px;
    width: 100%;

    ${breakpoint.small} {
        display: block;
    }
`;

const Location = styled.div`
    color: ${slate80};
    font: 14px/20px ${FontFamily.SharpSansMedium};
`;

const CardRow = styled(Row)`
    margin-top: 20px;
    ${(p) => (p.hideMobile ? 'display: none' : '')};

    ${breakpoint.small} {
        ${(p) => (p.hideMobile ? 'display: block' : '')};
        ${(p) => (p.hideDesktop ? 'display: none' : '')};
    }
`;

const Label = styled(Column)`
    ${columnSize(8)};
    color: ${slate80};
    font: 13px/20px ${FontFamily.SharpSansSemibold};
    margin-bottom: 4px;

    ${breakpoint.medium} {
        ${columnSize(24)};
    }
`;

const Body = styled(Column)`
    ${columnOffset(2)};
    ${columnSize(12)};
    font: 13px/20px ${FontFamily.SharpSansMedium};

    ${breakpoint.medium} {
        ${columnOffset(0)};
        ${columnSize(24)};
    }

    ${(p) => (p.hideMobile ? 'display: none' : '')};
`;

const Rating = styled.span`
    display: none;

    ${breakpoint.small} {
        display: inline-block;
    }
`;
